import { styled, alpha, type Theme } from '@mui/material/styles';
import { getTableRowHoverOverlay } from '../../../styles/theme/tableRowOverlays';
import {
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Stepper,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';
import { colors, spacing, typography } from '../../../styles/theme';

const transferFieldSurface = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: alpha(theme.palette.common.white, 0.06),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
          '& fieldset': {
            borderColor: theme.palette.success.main,
            borderWidth: '2px',
          },
        },
        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.12),
          '& fieldset': {
            borderColor: '#0BB296',
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.25)}`,
          },
        },
      }
    : {
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#ffffff',
          '& fieldset': {
            borderColor: theme.palette.success.main,
            borderWidth: '2px',
          },
        },
        '&.Mui-focused': {
          backgroundColor: '#ffffff',
          '& fieldset': {
            borderColor: '#0BB296',
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.12)}`,
          },
        },
      };

// Panel Overlay (fondo oscuro)
export const PanelOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1201,
  transition: 'opacity 300ms',
  '&.open': {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '&.closed': {
    opacity: 0,
    pointerEvents: 'none',
  },
}));

// Panel Container (panel lateral - optimizado)
export const PanelContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100%',
  width: '70%',
  maxWidth: '64rem', // Aumentado de 60rem a 64rem (1024px)
  backgroundColor: theme.palette.background.paper,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.65)'
      : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  zIndex: 1202,
  transform: 'translateX(100%)',
  transition: 'transform 300ms ease-in-out',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'auto',
  '&.open': {
    transform: 'translateX(0)',
  },
  '&.closed': {
    transform: 'translateX(100%)',
    pointerEvents: 'none',
  },
}));

// Panel Header (optimizado)
export const PanelHeader = styled(Box)(({ theme }) => ({
  padding: '20px 28px', // Aumentado de 12px 16px
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
}));

export const PanelTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem', // Aumentado de 1.125rem
  fontWeight: 600, // Aumentado de 500
  color: theme.palette.text.primary,
  margin: 0,
  letterSpacing: '-0.01em',
}));

// Botón de cerrar (optimizado)
export const CloseButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minWidth: 'auto',
  padding: '8px',
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
  '&:focus': {
    outline: 'none',
  },
}));

// Panel Content (optimizado)
export const PanelContent = styled(Box)(({ theme }) => ({
  padding: `0 ${spacing.xl} ${spacing.xl} ${spacing.xl}`, // Sin padding superior, mantener lateral e inferior
  overflowY: 'auto',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0, // Permite que el flex funcione correctamente
  backgroundColor: theme.palette.background.paper,
}));

// Panel Actions (optimizado)
export const PanelActions = styled(Box)(({ theme }) => ({
  padding: '24px 32px', // Optimizado: 24px vertical, 32px horizontal
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  gap: '12px', // Aumentado de spacing.sm (8px) a 12px
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexShrink: 0,
}));

export const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: spacing.lg,
  flexShrink: 0, // No se encoge, mantiene su tamaño
}));

export const StepContent = styled(Box)(({ theme }) => ({
  marginTop: spacing.xl, // Aumentado de spacing.lg a spacing.xl (32px)
  padding: `0 36px`, // Padding horizontal de 36px para mejor separación del borde
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0, // Permite que el flex funcione correctamente
}));

export const ProductListContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0, // Permite que el flex funcione correctamente
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px', // Espaciado entre items
}));

// Título de sección para productos
export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem', // 18px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

export const ProductItem = styled(Box)(({ theme }) => ({
  padding: '20px', // Aumentado de spacing.md (16px)
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  position: 'relative',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: getTableRowHoverOverlay(theme),
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`
        : '0 2px 8px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-1px)',
  },
}));

export const ProductInfo = styled(Box)(({ theme }) => ({
  flex: 1,
}));

export const ProductCode = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem', // 15px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '8px',
}));

export const ProductName = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // 14px
  color: theme.palette.text.secondary,
  marginBottom: '12px',
  lineHeight: 1.5,
}));

export const ProductDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '8px',
}));

export const ProductDetailChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem', // 12px
  height: 26, // Aumentado de 24
  borderRadius: '6px',
  fontWeight: 500,
}));

export const RemoveButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  position: 'absolute',
  top: '12px',
  right: '12px',
  padding: '8px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
  },
}));

export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: spacing.xl, // Aumentado de spacing.lg a spacing.xl (32px)
}));

// Título de sección del formulario
export const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem', // 18px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '24px',
}));

export const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // 14px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    minHeight: '48px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
      borderWidth: '1px',
    },
    ...transferFieldSurface(theme),
    '& textarea': {
      minHeight: '120px',
      maxHeight: '200px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.success.main,
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& .MuiFormHelperText-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    '& legend': {
      maxWidth: 'none',
      width: 'fit-content',
      minWidth: 0,
      padding: '0 8px',
      boxSizing: 'content-box',
    },
  },
}));

export const StyledFormControl = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    minHeight: '48px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: alpha(theme.palette.success.main, 0.45),
      borderWidth: '1px',
    },
    ...transferFieldSurface(theme),
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.success.main,
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& .MuiFormHelperText-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    '& legend': {
      maxWidth: 'none',
      width: 'fit-content',
      minWidth: 0,
      padding: '0 8px',
      boxSizing: 'content-box',
    },
  },
}));

// Contenedor principal del resumen - estilo dashboard
export const SummaryContainer = styled(Box)(({ theme }) => ({
  marginBottom: spacing.xl,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem', // 20px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '8px',
  letterSpacing: '-0.01em',
}));

// Grid para las cards principales (Productos, Destino y Notas - 3 columnas)
export const SummaryGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr', // 3 columnas iguales
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr', // 2 columnas en tablets
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr', // 1 columna en mobile
  },
}));

// Card individual estilo dashboard
export const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: '20px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

// Card para notas (ancho completo, más compacto)
export const SummaryNotesCard = styled(Paper)(({ theme }) => ({
  padding: '16px 20px', // Reducido padding vertical de 20px a 16px
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'row', // Cambiado a row para layout horizontal más compacto
  alignItems: 'center', // Centrar verticalmente
  gap: '12px',
}));

// Contenedor del icono con fondo de color
export const SummaryIconContainer = styled(Box)(({ theme }) => ({
  width: '48px', // Tamaño original
  height: '48px', // Tamaño original
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

// Icono container para productos (fondo púrpura)
export const SummaryIconProducts = styled(SummaryIconContainer)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#9333ea', 0.22) : '#f3e8ff',
  color: theme.palette.mode === 'dark' ? '#e9d5ff' : '#9333ea',
}));

// Icono container para destino (fondo azul)
export const SummaryIconLocation = styled(SummaryIconContainer)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#2563eb', 0.22) : '#dbeafe',
  color: theme.palette.mode === 'dark' ? '#bfdbfe' : '#2563eb',
}));

// Icono container para notas (fondo gris)
export const SummaryIconNotes = styled(SummaryIconContainer)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#f3f4f6',
  color: theme.palette.text.secondary,
}));

// Label de la card
export const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem', // 13px
  color: theme.palette.text.secondary,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginTop: '4px',
}));

// Valor principal (número grande)
export const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem', // 32px
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
  marginTop: '4px',
}));

// Valor secundario (texto)
export const SummaryValueText = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '8px',
}));

// Header de la card (icono + label)
export const SummaryCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

// Badge para números en el resumen (mejorado)
export const SummaryBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 16px',
  borderRadius: '8px',
  backgroundColor: colors.primary.main,
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 600,
  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
}));

// Chip para destino (mejorado)
export const SummaryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? alpha('#2563eb', 0.28) : '#dbeafe',
  color: theme.palette.mode === 'dark' ? '#bfdbfe' : '#1e40af',
  fontWeight: 600,
  height: '32px',
  fontSize: '0.875rem',
  borderRadius: '8px',
  boxShadow: '0 1px 2px rgba(37, 99, 235, 0.1)',
}));

// Texto de notas (más compacto)
export const SummaryNotesText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // 14px
  color: theme.palette.text.primary,
  lineHeight: 1.5, // Reducido de 1.6 a 1.5
  fontStyle: 'italic',
  flex: 1, // Ocupar espacio disponible
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2, // Máximo 2 líneas
  WebkitBoxOrient: 'vertical',
  '&.empty': {
    color: theme.palette.text.secondary,
  },
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableHead-root': {
    backgroundColor: theme.palette.background.paper,
  },
}));

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& .MuiTableCell-root': {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '0.875rem', // 14px
    letterSpacing: 'normal',
    textTransform: 'none', // Sin mayúsculas
    borderBottom: `1px solid ${theme.palette.divider}`, // Línea solo en el header
    padding: '16px 20px',
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.04)
        : theme.palette.background.paper,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '16px 20px', // Padding generoso
  borderBottom: 'none', // Sin líneas divisoras entre filas
  fontSize: '0.875rem', // 14px
  backgroundColor: theme.palette.background.paper,
  '&:first-of-type': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

export const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '16px 20px',
  borderBottom: `1px solid ${theme.palette.divider}`, // Línea solo en el header
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  letterSpacing: 'normal',
  textTransform: 'none', // Sin mayúsculas
}));

// Título de sección para tabla de productos
export const ProductsTableTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem', // 18px
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginTop: spacing.xl,
  marginBottom: '16px',
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  minHeight: '44px', // Altura consistente
  padding: '12px 24px',
  fontSize: '0.875rem',
}));

export const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: theme.palette.common.white,
  padding: '12px 32px', // Padding horizontal aumentado
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

// Color/fondo/hover vienen de variant="tonal" del tema; aquí solo tamaño/spacing.
export const SecondaryButton = styled(ActionButton)({
  padding: '12px 24px',
});

export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing.xl,
  textAlign: 'center',
}));

export const LoadingText = styled(Typography)(({ theme }) => ({
  marginTop: spacing.md,
  fontSize: typography.fontSize.sm,
  color: theme.palette.text.secondary,
}));

export const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: spacing.md,
  marginTop: spacing.md, // Agregar margen superior para separarlo del header
  borderRadius: spacing.sm,
  flexShrink: 0, // No se encoge, mantiene su tamaño
}));

export const WarningBox = styled(Box)(({ theme }) => ({
  padding: spacing.md,
  backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
  border: `1px solid ${alpha(theme.palette.warning.main, 0.55)}`,
  borderRadius: spacing.sm,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  marginBottom: spacing.md,
}));

export const WarningText = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.sm,
  color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
  fontWeight: 500,
}));

// Contenedor unificado para ProgressBar y StepIndicators
export const StepsContainer = styled(Box)(({ theme }) => ({
  padding: '8px 0', // Padding vertical mínimo (8px), sin padding horizontal (0px)
  width: '100%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : theme.palette.background.paper,
  borderRadius: '12px',
  marginTop: '0', // Sin margen superior para acercarlo al header
  marginBottom: '24px',
  flexShrink: 0,
}));

// Estilos mejorados para ProgressBar y StepIndicators con mejor UX/UI
export const CompactProgressBarContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px', // Espaciado entre ProgressBar y StepIndicators
  width: '100%',
  // Eliminar todo el padding del ProgressBarContainer interno para maximizar el ancho
  '& > div[class*="ProgressBarContainer"]': {
    padding: '4px 0 !important', // Padding mínimo vertical (4px), sin padding horizontal (0px)
    marginBottom: '0 !important', // Eliminar margin bottom del contenedor
    width: '100% !important',
  },
  '& > div': {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  // Asegurar que ProgressBarTrack ocupe todo el ancho
  '& > div > div[class*="ProgressBarTrack"]': {
    height: '8px !important', // Altura optimizada
    width: '100% !important',
    borderRadius: '8px !important',
    margin: '0 !important', // Sin márgenes
  },
  '& > div > div[class*="ProgressBarFill"]': {
    width: '100% !important',
  },
  // Ajustar el contenedor de información
  '& > div > div[class*="ProgressInfoContainer"]': {
    marginTop: '4px !important', // Reducir espacio arriba del texto
    padding: '0 !important', // Sin padding
  },
}));

export const CompactStepIndicatorsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  '& > div': {
    marginBottom: '0 !important',
    padding: `0 ${theme.spacing(1)} !important`,
    gap: '40px !important', // Espaciado horizontal entre pasos
  },
  '& > div > div': {
    '& > div:first-of-type': {
      width: '52px !important',
      height: '52px !important',
      marginBottom: `${theme.spacing(1)} !important`,
      boxShadow:
        theme.palette.mode === 'dark'
          ? `0 2px 8px ${alpha(theme.palette.common.black, 0.45)} !important`
          : '0 2px 8px rgba(0, 0, 0, 0.1) !important',
    },
    '& > p, & > *[class*="Typography"], & > *': {
      fontSize: '13px !important',
      fontWeight: '400 !important', // Sin negrilla - cambiado de 500/600 a 400
      lineHeight: '1.4 !important',
      minHeight: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  // Sobrescribir el fontWeight que viene del componente StepIndicators (aplicado via sx prop)
  '& p[class*="MuiTypography"], & span[class*="MuiTypography"]': {
    fontWeight: '400 !important',
  },
  '& svg': {
    width: '22px !important',
    height: '22px !important',
  },
}));
